import { erkenneRechtsformImKatalog, type RechtsformBefund } from "@kmvs/forms-core";
import { katalogLaden } from "./katalogCache";

export type LegalFormOption = {
  id: number;
  legalForm: string;
  /** Kurzform für die Anzeige („GmbH", „GdWE"). */
  kuerzel?: string;
  /**
   * Bei „Sonstige" und „Ausländische Rechtsform" ist die konkrete Bezeichnung
   * die eigentliche Information — dort blendet die Auswahl ein Freitextfeld ein.
   */
  erlaubtFreitext?: boolean;
  /**
   * Begriff für die Benennung der Verbindungsliste („Unternehmen", „Verein",
   * „Stiftung" …) — siehe utils/unternehmensBegriff.ts.
   */
  verbindungLabel?: string;
};

export const normalizeLegalFormOptions = (payload: any): LegalFormOption[] => {
  const list: any[] = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
      ? payload.items
      : [];

  return list
    .map((entry: any) => ({
      id: Number(entry?.id),
      legalForm: String(entry?.legalForm ?? entry?.name ?? ""),
      kuerzel: entry?.kuerzel ? String(entry.kuerzel) : undefined,
      erlaubtFreitext: !!entry?.erlaubtFreitext,
      verbindungLabel: entry?.verbindungLabel ? String(entry.verbindungLabel) : undefined,
    }))
    .filter((entry) => Number.isFinite(entry.id) && entry.legalForm.trim().length > 0);
};

/**
 * Rechtsform aus dem Firmennamen ableiten. Die Regeln liegen in
 * `@kmvs/forms-core` (`legalForm.ts`) — dieselbe Tabelle, die auch der Backfill
 * benutzt.
 *
 * Ersetzt die frühere Übereinstimmungssuche gegen die Katalog-**Labels**: die
 * ließ den längsten Treffer gewinnen („Muster GmbH & Co. KG" wurde zur GmbH) und
 * suchte nach Beschreibungstexten, die in keinem Firmennamen vorkommen
 * („Aktiengesellschaft (AG)"). Gemessen an den gepflegten PROD-Firmen: 7,4 %
 * Falschzuordnungen gegen 2,1 % der Kaskade.
 *
 * `option` ist nur gefüllt, wenn der Katalog die erkannte Rechtsform überhaupt
 * kennt; `befund.konfidenz` sagt, ob der Aufrufer sie setzen darf (`SICHER`) oder
 * nur anbieten (`UNSICHER`).
 */
export const rechtsformAusName = (
  name: string,
  options: LegalFormOption[],
): { befund: RechtsformBefund; option: LegalFormOption | null } => {
  const { befund, eintrag } = erkenneRechtsformImKatalog(name, options);
  // „… n.e.V." endet auch auf „e.V." — die Kaskade (forms-core, deckungsgleich
  // mit Backfill-Migration und API) kennt den n.e.V. nicht und machte daraus
  // SICHER einen eingetragenen Verein. Solange sie ihn nicht führt, wird er
  // hier vorgezogen (User 28.09.2026) — nur wenn der Katalog ihn hat.
  if (befund.key === "EV" && NEV_MUSTER.test(name ?? "")) {
    const nev = options.find(istNichtEingetragenerVerein) ?? null;
    return { befund, option: nev };
  }
  return { befund, option: eintrag };
};

/** „n.e.V." bzw. „nicht eingetragener Verein" am Namensende. */
const NEV_MUSTER = /(\bn\.\s*e\.\s*v\.?|nicht eingetragener verein)[\s.,;:)-]*$/i;

/** Der Katalogeintrag des nicht eingetragenen Vereins (Kürzel oder Name). */
export const istNichtEingetragenerVerein = (o: LegalFormOption): boolean =>
  (o.kuerzel ?? "").trim().toLowerCase() === "n.e.v." || /nicht eingetragener verein/i.test(o.legalForm);

export const fetchLegalFormOptions = async (): Promise<LegalFormOption[]> => {
  // Geteilt und gecacht (utils/katalogCache.ts) — die Akte fragte den Katalog
  // bis 28.09.2026 viermal ab.
  const payload = await katalogLaden("contact-legal-form");
  return normalizeLegalFormOptions(payload);
};
